import React, { useEffect, useRef, useState } from 'react';
import { OVERLAY_PANEL_GAP } from '../../../pages/Shoreline/theme';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useErrorNotice } from '../../../../hooks/useErrorNotice';
import moment from 'moment';
import { Actions as Map } from '../../../../store/ducks/map';
import { Waves, GestureOutlined, LinearScale } from '@material-ui/icons';
import { computeProjections, buildMeanShorelineFeature, getShorelineDates, detectOutlierShorelineDates } from './utils';
import * as Metadata from '../../../../common/metadata';

import { Actions as Imagery } from '../../../../store/ducks/imagery';
import { Actions as Results } from '../../../../store/ducks/results';
import {
  Box,
  Grow,
  Card,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import { lineToPoints, circleToPolyline } from '../../../../algorithms/utils/turf';
import {
  MIN_TRANSECT_STEP,
  MAX_TRANSECT_STEP,
  MIN_TRANSECT_EXTENT,
  MAX_TRANSECT_EXTENT,
} from '../../../../common/utils';
import * as turf from '@turf/turf';
import { TerraDrawManager } from '../../../../common/TerraDrawManager';
import ShapeEditControl from '../../map/ShapeEditControl';
import { BASELINE_VERTEX_EDITING_ENABLED, useShapeVertexEditing } from '../../../../hooks/useShapeVertexEditing';

import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SimpleInfoDialog from '../../dialog/SimpleInfoDialog';
import ProjectionSettingsCard from './ProjectionSettingsCard';
import ShoreRExportDialog from '../../dialog/ShoreRExportDialog';
import OutlierShorelinesDialog from './OutlierShorelinesDialog';

/**
 * @function ShoreRAnalysisSteps
 * @description ShoreR module's main analysis workflow: draw/import the baseline, configure and
 * generate transects, run the coastline analysis, then configure and display shoreline
 * projections (smoothed rate-based and/or Bruun rule), with outlier shoreline detection/removal
 * and export to shapefile.
 * @param {Object} props - Component properties.
 * @param {Function} props.t - Translation function.
 * @param {Object} props.mapRef - Ref to the Google Maps instance.
 * @param {Object} props.roiCoords - Ref to the ROI polygon.
 * @param {Object} props.bounds - Ref to the map bounds.
 * @param {Object} props.baseLine - Current baseline state.
 * @param {Function} props.setBaseLine - Baseline state setter.
 * @param {Object} props.drawingManager - Ref to the TerraDrawManager.
 * @param {boolean} props.baselineVisible - Whether the baseline overlay should be visible.
 * @param {Function} props.onProjectionLegendChange - Called when the projection legend changes.
 * @see {@link handleDrawBaseline} - starts drawing the baseline.
 * @see {@link generateTransects} - creates transects along the baseline.
 * @see {@link handleProjectionConfirm} - runs and plots the shoreline projections.
 * **Called by:
 * @see {ProcessingShoreRPage}
 * @memberof module:ShoreR
 * @returns {JSX.Element} The ShoreR analysis workflow UI.
 */
const ShoreRAnalysisSteps = ({
  t,
  mapRef,
  roiCoords,
  bounds,
  baseLine,
  setBaseLine,
  drawingManager,
  baselineVisible,
  onProjectionLegendChange,
}) => {
  const [settingsTransectsEnabled, setSettingsTransectsEnabled] = useState(false);
  const [drawingEnabled, setDrawingEnabled] = useState(false);
  const [analysisDisabled, setAnalysisDisabled] = useState(false);
  const [analysisCompleted, setAnalysisCompleted] = useState(false);
  const [confirmDrawing, setConfirmDrawing] = useState(false);
  const [transectsStep, setTransectsStep] = useState(100);
  const [transectsExtent, setTransectsExtent] = useState(1600);
  const [transectsObjects, setTransectsObjects] = useState([]);
  const [isSettings, setIsSettings] = useState(false);
  const [showImageChooserHelp, setShowImageChooserHelp] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [threshold, setThreshold] = useState(0.01);
  const [drawingBaseline, setDrawingBaseline] = useState(false);
  const [baseLineCoords, setBaseLineCoords] = useState([]);
  const [confirmTransectsConfigEnabled, setConfirmTransectsConfigEnabled] = useState(true);
  const [projectionConfirmed, setProjectionConfirmed] = useState(false);
  const [bruunActive, setBruunActive] = useState(false);
  const [bruunConfirmed, setBruunConfirmed] = useState(false);
  const [bruunHorizon, setBruunHorizon] = useState(null);
  const [bruunSLRa, setBruunSLRa] = useState(0);
  const [bruunL, setBruunL] = useState('');
  const [bruunHStar, setBruunHStar] = useState('');
  const [bruunDC, setBruunDC] = useState('');
  const [bruunHelpOpen, setBruunHelpOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [outlierShorelines, setOutlierShorelines] = useState([]);
  const [outlierDialogOpen, setOutlierDialogOpen] = useState(false);
  const outlierDialogShownRef = useRef(false);
  const dispatch = useDispatch();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const hasShoreRData = useSelector((state) => Boolean(state.results.shoreRData));
  const shoreRData = useSelector((state) => state.results.shoreRData);

  const selectedImages = useSelector((state) => state.results.chosenImages, shallowEqual);

  const availableImages = useSelector((state) => state.acquisition.availableDates, shallowEqual);

  const imageMetadata = useSelector((state) => state.acquisition.metadata, shallowEqual);

  const mapShapes = useSelector((state) => state.map.shapes, shallowEqual);

  const projectionPolylinesRef = useRef([]);
  const projectionPolylinesByHorizonRef = useRef({});
  const projectionStateRef = useRef(null);
  const legendGroupRef = useRef(null);
  const bruunPolylinesRef = useRef([]);

  const EROSION_CATEGORIES = [
    { key: 'Stable', nameKey: 'forms.map.coastlineShoreR.stable' },
    { key: 'Accreted', nameKey: 'forms.map.coastlineShoreR.accreted' },
    { key: 'Eroded', nameKey: 'forms.map.coastlineShoreR.eroded' },
    { key: 'Critically Eroded', nameKey: 'forms.map.coastlineShoreR.criticallyEroded' },
  ];

  /**
   * @function handleProjectionConfirm
   * @description Computes and plots the smoothed rate-based shoreline projections for the given
   * horizons/method.
   * 1. Clears any previous projection/Bruun overlays.
   * 2. Resolves the reference shoreline (latest, a chosen date, or the synthetic yearly-mean
   *    shoreline) per `referenceConfig.mode`.
   * 3. Computes each horizon's projected points and dispatches them to the results state.
   * 4. Draws each horizon's projection as dotted polyline segments (split by erosion label,
   *    colored per `Metadata.ESTEVES_LABELS`), and builds the map legend group for them.
   * @param {Array<Number>} horizons - Projection horizons, in years.
   * @param {String} method - Projection method key.
   * @param {Object} [referenceConfig={mode: "latest"}] - Reference shoreline configuration (mode, dateIndex).
   * @see {@link computeProjections}
   * @see {@link buildMeanShorelineFeature}
   * @see {@link makeDottedPolyline}
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via ProjectionSettingsCard's onConfirm prop.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleProjectionConfirm = (horizons, method, referenceConfig = { mode: 'latest' }) => {
    projectionPolylinesRef.current.forEach((p) => p.setMap(null));
    projectionPolylinesRef.current = [];
    bruunPolylinesRef.current.forEach((p) => p.setMap(null));
    bruunPolylinesRef.current = [];
    projectionPolylinesByHorizonRef.current = {};
    setBruunConfirmed(false);
    setBruunActive(false);

    if (!shoreRData?.transectData) return;

    const features = Array.isArray(shoreRData.transectData)
      ? shoreRData.transectData
      : (shoreRData.transectData?.features ?? []);

    const shorelineFeatures = shoreRData.shorelinesGeoJSON?.features ?? [];
    const hasShorelines = shorelineFeatures.length > 0;

    let referenceShoreline = null;
    if (hasShorelines) {
      if (referenceConfig.mode === 'yearlyMean') {
        referenceShoreline = buildMeanShorelineFeature(shoreRData.transectData, shoreRData.shorelinesGeoJSON);
        if (!referenceShoreline) {
          referenceShoreline = shorelineFeatures[shorelineFeatures.length - 1];
        }
      } else if (referenceConfig.mode === 'byDate') {
        const idx = referenceConfig.dateIndex;
        referenceShoreline = (idx != null && shorelineFeatures[idx]) || shorelineFeatures[shorelineFeatures.length - 1];
      } else {
        referenceShoreline = shorelineFeatures[shorelineFeatures.length - 1];
      }
    }

    const projections = computeProjections(
      shoreRData.transectData,
      horizons,
      method,
      referenceShoreline,
      referenceConfig.mode,
    );

    dispatch(
      Results.push('shoreRProjections', {
        horizons,
        method,
        referenceMode: referenceConfig.mode,
        referenceFeature: referenceShoreline,
        projections,
      }),
    );

    const polylinesByHorizon = {};
    horizons.forEach((h) => {
      polylinesByHorizon[h] = [];
    });

    /**
     * @function makeDottedPolyline
     * @description Draws a dotted polyline segment for one erosion-label run of a horizon's
     * projected points, colored per the label, and registers it in the horizon's polyline group.
     * @param {Array<Object>} segmentPoints - Consecutive projected points sharing the same label.
     * @param {String} label - Erosion category label (from Metadata.ESTEVES_LABELS).
     * @param {Number} horizon - Projection horizon this segment belongs to.
     * **Called by:
     * @see {handleProjectionConfirm}
     * @memberof module:ShoreR
     * @returns {void}
     */
    const makeDottedPolyline = (segmentPoints, label, horizon) => {
      const color = Metadata.ESTEVES_LABELS[label]?.color ?? '#888';
      const polyline = new window.google.maps.Polyline({
        path: segmentPoints,
        strokeColor: color,
        strokeOpacity: 0,
        strokeWeight: 2,
        geodesic: true,
        map: mapRef.current,
        icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 2 }, offset: '0', repeat: '10px' }],
      });
      projectionPolylinesRef.current.push(polyline);
      if (polylinesByHorizon[horizon]) polylinesByHorizon[horizon].push(polyline);
    };

    horizons.forEach((horizon) => {
      const transectProj = projections.find((p) => p.horizon === horizon);
      if (!transectProj) return;

      const pointsById = {};
      transectProj.points.forEach((p) => {
        pointsById[p.id] = p;
      });

      let currentLabel = null;
      let segmentPoints = [];

      features.forEach((feature) => {
        const point = pointsById[feature.id];
        if (!point) return;
        const label = feature.properties?.label;

        if (label !== currentLabel) {
          if (currentLabel !== null && segmentPoints.length >= 1) {
            segmentPoints.push(point);
            if (segmentPoints.length >= 2) makeDottedPolyline(segmentPoints, currentLabel, horizon);
          }
          currentLabel = label;
          segmentPoints = [point];
        } else {
          segmentPoints.push(point);
        }
      });

      if (segmentPoints.length >= 2 && currentLabel) {
        makeDottedPolyline(segmentPoints, currentLabel, horizon);
      }
    });

    projectionPolylinesByHorizonRef.current = polylinesByHorizon;
    projectionStateRef.current = { horizons, method, referenceShoreline, referenceMode: referenceConfig.mode };

    const legendGroup = {
      id: 'projections',
      name: t('forms.shoreR.projection.legendLabel'),
      subtitle: t('forms.shoreR.projection.methods.' + method),
      type: 'group',
      singleGroupToggle: false,
      children: horizons
        .filter((h) => polylinesByHorizon[h]?.length > 0)
        .map((h) => ({
          id: `proj-horizon-${h}`,
          name: `${h} ${t('forms.shoreR.projection.yearUnit')}`,
          visible: true,
          polylines: polylinesByHorizon[h],
        })),
    };

    legendGroupRef.current = legendGroup;

    if (onProjectionLegendChange) {
      onProjectionLegendChange(legendGroup.children.length > 0 ? [legendGroup] : []);
    }

    setProjectionConfirmed(true);
  };

  /**
   * @function handleRedoAnalysis
   * @description Fully resets the ShoreR workflow back to the baseline-drawing step: clears
   * projection/Bruun overlays, transects, and baseline, and dispatches the removal of all cached
   * analysis results/shapes.
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via the "Redo analysis" button's onClick.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleRedoAnalysis = () => {
    projectionPolylinesRef.current.forEach((p) => p.setMap(null));
    projectionPolylinesRef.current = [];
    bruunPolylinesRef.current.forEach((p) => p.setMap(null));
    bruunPolylinesRef.current = [];
    projectionPolylinesByHorizonRef.current = {};
    projectionStateRef.current = null;
    legendGroupRef.current = null;
    setBruunConfirmed(false);
    setBruunActive(false);

    transectsObjects.forEach((o) => o.setMap(null));
    setTransectsObjects([]);

    if (baseLine) baseLine.overlay.setMap(null);
    setBaseLine(null);
    setBaseLineCoords([]);

    setProjectionConfirmed(false);
    setAnalysisCompleted(false);
    setAnalysisDisabled(false);
    setDrawingEnabled(false);
    setConfirmDrawing(false);
    setSettingsTransectsEnabled(false);
    setIsSettings(false);
    setOutlierShorelines([]);
    setOutlierDialogOpen(false);
    outlierDialogShownRef.current = false;

    dispatch(Results.push('shoreRData', null));
    dispatch(Results.push('shoreRProjections', null));
    dispatch(Results.push('chosenImages', null));
    dispatch(Map.removeShapeGroup('shoreRCoastlineData'));
    dispatch(Map.removeShapeGroup('transect'));
    setTransectsStep(100);
    setTransectsExtent(1600);
    if (onProjectionLegendChange) onProjectionLegendChange([]);
  };

  /**
   * @function handleOutlierConfirm
   * @description Closes the outlier dialog and commits the analysis, excluding the selected
   * outlier shoreline dates.
   * @param {Array<Number>} timeStartsToRemove - timeStart values of the shorelines to exclude.
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via OutlierShorelinesDialog's onConfirm prop.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleOutlierConfirm = (timeStartsToRemove) => {
    setOutlierDialogOpen(false);
    dispatch(Imagery.performShoreRAnalysisCommit(timeStartsToRemove));
  };

  /**
   * @function findShorelineOverlay
   * @description Finds the Google Maps polyline overlay for a given outlier date within the
   * rendered shoreline group, matching by calendar day.
   * @param {Object} outlier - Outlier shoreline entry, with a `date`.
   * **Called by:
   * @see {handleOutlierToggle}
   * @memberof module:ShoreR
   * @returns {google.maps.Polyline|null} The matching overlay, or null if not found.
   */
  const findShorelineOverlay = (outlier) => {
    const group = mapShapes?.find((s) => s.group === 'shoreRCoastlineData');
    if (!group) return null;
    const targetDay = moment.utc(outlier.date).format('YYYY-MM-DD');
    const idx = group.content?.findIndex(
      (feature) => moment.utc(feature?.properties?.date).format('YYYY-MM-DD') === targetDay,
    );
    if (idx == null || idx < 0) return null;
    return group.overlays?.[idx]?.overlay ?? null;
  };

  /**
   * @function handleOutlierToggle
   * @description Hides (or restores) the selected outlier date's shoreline overlay on the map, so
   * the user can preview the effect of excluding it.
   * @param {Object} outlier - Outlier shoreline entry, with a `date`.
   * @param {boolean} checked - Whether the outlier is selected for removal.
   * @see {@link findShorelineOverlay}
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via OutlierShorelinesDialog's onToggle prop.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleOutlierToggle = (outlier, checked) => {
    const overlay = findShorelineOverlay(outlier);
    if (overlay) overlay.setMap(checked ? null : mapRef.current);
  };

  /**
   * @function restoreAllShorelines
   * @description Restores every shoreline overlay's visibility on the map.
   * **Called by:
   * @see {handleOutlierClose}
   * @memberof module:ShoreR
   * @returns {void}
   */
  const restoreAllShorelines = () => {
    const group = mapShapes?.find((s) => s.group === 'shoreRCoastlineData');
    group?.overlays?.forEach((wrapper) => wrapper?.overlay?.setMap(mapRef.current));
  };

  /**
   * @function handleOutlierClose
   * @description Restores all shoreline overlays and closes the outlier dialog without committing.
   * @see {@link restoreAllShorelines}
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via OutlierShorelinesDialog's onClose prop.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleOutlierClose = () => {
    restoreAllShorelines();
    setOutlierDialogOpen(false);
  };

  /**
   * @function handleRedoLastStep
   * @description Clears the current projection/Bruun overlays and legend, without resetting the
   * whole analysis, so the user can reconfigure the projection.
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via the "Redo projection" button's onClick.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleRedoLastStep = () => {
    projectionPolylinesRef.current.forEach((p) => p.setMap(null));
    projectionPolylinesRef.current = [];
    bruunPolylinesRef.current.forEach((p) => p.setMap(null));
    bruunPolylinesRef.current = [];
    projectionPolylinesByHorizonRef.current = {};
    projectionStateRef.current = null;
    legendGroupRef.current = null;
    setBruunConfirmed(false);
    setBruunActive(false);
    if (onProjectionLegendChange) onProjectionLegendChange([]);
    dispatch(Results.push('shoreRProjections', null));
    setProjectionConfirmed(false);
  };

  /**
   * @function handleBruunOpen
   * @description Opens the Bruun rule configuration panel, defaulting the Bruun horizon to the
   * first confirmed projection horizon.
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via the "Apply Bruun rule" button's onClick.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleBruunOpen = () => {
    const hs = projectionStateRef.current?.horizons ?? [];
    if (hs.length > 0) setBruunHorizon(hs[0]);
    setBruunActive(true);
  };

  /**
   * @function handleBruunConfirm
   * @description Applies the Bruun rule's relative sea-level-adjusted retreat (Ra) as an
   * additional offset on top of the selected horizon's rate-based projection, and re-plots that
   * horizon's projection with the Bruun-adjusted points.
   * 1. Computes `Ra_m = (SLRa * L) / (H* + DC) / 1000`, the Bruun retreat in meters.
   * 2. Recomputes the projection for the selected horizon with `Ra_m` as the `raOffset`.
   * 3. Redraws that horizon's dotted polyline segments (split by erosion label) using the
   *    Bruun-adjusted points, and updates the map legend to show both the original and Bruun layers.
   * @see {@link computeProjections}
   * @see {@link makeBruunPolyline}
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via the Bruun panel's "Confirm" button.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleBruunConfirm = () => {
    if (!projectionStateRef.current || !shoreRData?.transectData) return;
    const { method, referenceShoreline, referenceMode } = projectionStateRef.current;

    const Ra_m = (bruunSLRa * Number(bruunL)) / (Number(bruunHStar) + Number(bruunDC)) / 1000;

    projectionPolylinesRef.current.forEach((p) => p.setMap(null));
    const selectedPolylines = projectionPolylinesByHorizonRef.current[bruunHorizon] ?? [];
    selectedPolylines.forEach((p) => p.setMap(mapRef.current));

    const bruunProjections = computeProjections(
      shoreRData.transectData,
      [bruunHorizon],
      method,
      referenceShoreline,
      referenceMode,
      Ra_m,
    );

    bruunPolylinesRef.current.forEach((p) => p.setMap(null));
    bruunPolylinesRef.current = [];

    const features = Array.isArray(shoreRData.transectData)
      ? shoreRData.transectData
      : (shoreRData.transectData?.features ?? []);

    const bruunProj = bruunProjections.find((p) => p.horizon === bruunHorizon);
    if (bruunProj) {
      const pointsById = {};
      bruunProj.points.forEach((p) => {
        pointsById[p.id] = p;
      });

      let currentLabel = null;
      let segmentPoints = [];

      /**
       * @function makeBruunPolyline
       * @description Draws a dotted polyline segment for one erosion-label run of the
       * Bruun-adjusted projection's points, colored per the label.
       * @param {Array<Object>} pts - Consecutive Bruun-adjusted points sharing the same label.
       * @param {String} label - Erosion category label (from Metadata.ESTEVES_LABELS).
       * **Called by:
       * @see {handleBruunConfirm}
       * @memberof module:ShoreR
       * @returns {void}
       */
      const makeBruunPolyline = (pts, label) => {
        const color = Metadata.ESTEVES_LABELS[label]?.color ?? '#888';
        const polyline = new window.google.maps.Polyline({
          path: pts,
          strokeColor: color,
          strokeOpacity: 0,
          strokeWeight: 3,
          geodesic: true,
          map: mapRef.current,
          icons: [{ icon: { path: 'M 0,-2 0,2', strokeOpacity: 1, scale: 2 }, offset: '0', repeat: '16px' }],
        });
        bruunPolylinesRef.current.push(polyline);
      };

      features.forEach((feature) => {
        const point = pointsById[feature.id];
        if (!point) return;
        const label = feature.properties?.label;
        if (label !== currentLabel) {
          if (currentLabel !== null && segmentPoints.length >= 1) {
            segmentPoints.push(point);
            if (segmentPoints.length >= 2) makeBruunPolyline(segmentPoints, currentLabel);
          }
          currentLabel = label;
          segmentPoints = [point];
        } else {
          segmentPoints.push(point);
        }
      });
      if (segmentPoints.length >= 2 && currentLabel) {
        makeBruunPolyline(segmentPoints, currentLabel);
      }
    }

    const updatedLegend = {
      id: 'projections',
      name: t('forms.shoreR.projection.legendLabel'),
      subtitle: t('forms.shoreR.projection.methods.' + method),
      type: 'group',
      singleGroupToggle: false,
      children: [
        {
          id: `proj-horizon-${bruunHorizon}`,
          name: `${bruunHorizon} ${t('forms.shoreR.projection.yearUnit')}`,
          visible: true,
          polylines: selectedPolylines,
        },
        ...(bruunPolylinesRef.current.length > 0
          ? [
            {
              id: 'proj-bruun',
              name: `${t('forms.shoreR.bruun.legendLabel')} (${bruunHorizon} ${t('forms.shoreR.projection.yearUnit')})`,
              visible: true,
              polylines: bruunPolylinesRef.current,
            },
          ]
          : []),
      ],
    };

    if (onProjectionLegendChange) onProjectionLegendChange([updatedLegend]);
    setBruunConfirmed(true);
    setBruunActive(false);
  };

  /**
   * @function handleUndoBruun
   * @description Removes the Bruun-adjusted polylines, restores the original rate-based
   * projection's polylines, and resets the legend to the pre-Bruun state.
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via the "Undo Bruun" button's onClick.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleUndoBruun = () => {
    bruunPolylinesRef.current.forEach((p) => p.setMap(null));
    bruunPolylinesRef.current = [];
    projectionPolylinesRef.current.forEach((p) => p.setMap(mapRef.current));
    if (onProjectionLegendChange && legendGroupRef.current) {
      onProjectionLegendChange([legendGroupRef.current]);
    }
    setBruunConfirmed(false);
  };

  const TRANSECT_CONFIG = {
    CIRCLE_RADIUS: 1,
    POLYLINE_STYLE: {
      strokeColor: 'purple',
      strokeOpacity: 1.0,
      strokeWeight: 3,
      geodesic: true,
      clickable: true,
      zIndex: 90,
    },
  };

  /**
   * @function generateTransects
   * @description Generates evenly-spaced transects perpendicular to the baseline.
   * For each sample point along the baseline: finds the baseline's intersection near that point,
   * builds a short segment through it, rotates copies of it ±90° to get the two perpendicular
   * sides, then scales the result by `transectsExtent` to get the final transect length.
   * @see {@link lineToPoints}
   * @see {@link circleToPolyline}
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via the transectsStep/transectsExtent effect.
   * @memberof module:ShoreR
   * @returns {Array<Object>} Transects: {transect, center, sides: [{side, coords}, {side, coords}]}.
   */
  const generateTransects = () => {
    var points = lineToPoints(baseLine.overlay, transectsStep);
    var outputTransects = [];
    points.forEach((point, i) => {
      if (i === 0) return;
      const circle = circleToPolyline(
        new window.google.maps.Circle({
          center: point,
          radius: TRANSECT_CONFIG.CIRCLE_RADIUS,
        }),
      );

      let circleLineCoords = circle.map((coord) => {
        const { lat, lng } = coord.toJSON();
        return [lng, lat];
      });

      let turfCircle = turf.lineString(circleLineCoords);
      let turfBaseLine = turf.lineString(baseLineCoords);
      let turfIntersection = turf.lineIntersect(turfCircle, turfBaseLine);

      let turfTransect = turf.lineString([
        [turfIntersection.features[0].geometry?.coordinates[0], turfIntersection.features[0].geometry?.coordinates[1]],
        [point.toJSON().lng, point.toJSON().lat],
      ]);
      const pivot = [point.toJSON().lng, point.toJSON().lat];
      let turfTransectRotate = turf.transformRotate(turf.clone(turfTransect), 90, { pivot });
      let turfTransectRotate2 = turf.transformRotate(turf.clone(turfTransect), -90, { pivot });

      let turfFullTransect = turf.multiLineString([
        turfTransectRotate.geometry?.coordinates,
        turfTransectRotate2.geometry?.coordinates,
      ]);

      let scaleTransect = turf.transformScale(turfFullTransect, transectsExtent / 2);

      outputTransects.push({
        transect: i,
        center: Object.values(point.toJSON()).reverse(),
        sides: [
          {
            side: 1,
            coords: scaleTransect.geometry?.coordinates[0],
          },
          {
            side: 2,
            coords: scaleTransect.geometry?.coordinates[1],
          },
        ],
      });
    });

    return outputTransects;
  };

  useEffect(() => {
    if (!hasShoreRData) {
      console.log('[outlier] sem shoreRData');
      return;
    }
    setConfirmDrawing(false);
    setAnalysisCompleted(true);
    setAnalysisDisabled(true);
    transectsObjects.forEach((object) => object.setMap(null));
    setTransectsObjects([]);

    if (outlierDialogShownRef.current) {
      console.log('[outlier] já exibido (ref guard)');
      return;
    }
    const flagged = detectOutlierShorelineDates(shoreRData?.transectData, shoreRData?.shorelinesGeoJSON);
    console.log('[outlier] flagged:', flagged, {
      transects: shoreRData?.transectData,
      shorelines: shoreRData?.shorelinesGeoJSON,
    });
    if (flagged.length > 0) {
      const thumbnailByDay = {};
      (imageMetadata || []).forEach((meta) => {
        if (!meta?.thumbnail) return;
        const key = moment.utc(meta.date).format('YYYY-MM-DD');
        if (thumbnailByDay[key] === undefined) thumbnailByDay[key] = meta.thumbnail;
      });
      const flaggedWithThumb = flagged.map((outlier) => ({
        ...outlier,
        thumbnail: thumbnailByDay[moment.utc(outlier.date).format('YYYY-MM-DD')] ?? null,
      }));
      setOutlierShorelines(flaggedWithThumb);
      setOutlierDialogOpen(true);
      outlierDialogShownRef.current = true;
    }
  }, [hasShoreRData]);

  useEffect(() => {
    const isInvalid =
      transectsExtent < MIN_TRANSECT_EXTENT ||
      transectsExtent > MAX_TRANSECT_EXTENT ||
      transectsStep < MIN_TRANSECT_STEP ||
      transectsStep > MAX_TRANSECT_STEP;
    setConfirmTransectsConfigEnabled(!isInvalid);
    if (baseLineCoords.length < 2) return;
    transectsObjects.forEach((object) => object.setMap(null));
    setTransectsObjects([]);
    if (isInvalid) return;
    let newTransects = generateTransects();
    let tempTransectsObjects = [];
    newTransects.forEach((object) => {
      let sidesMaps0 = object.sides[0].coords.map((arr) => {
        return { lat: arr[1], lng: arr[0] };
      });
      let sidesMaps1 = object.sides[1].coords.map((arr) => {
        return { lat: arr[1], lng: arr[0] };
      });

      let complete = [sidesMaps0.flat(), sidesMaps1.flat()].flat();

      const polyline = new window.google.maps.Polyline({
        path: complete,
        transect: object.transect,
        ...TRANSECT_CONFIG.POLYLINE_STYLE,
      });

      polyline.setMap(mapRef.current);
      tempTransectsObjects.push(polyline);
    });

    setTransectsObjects(tempTransectsObjects);
  }, [transectsStep, transectsExtent]);

  useEffect(() => {
    if (Array.isArray(selectedImages) && selectedImages.length > 0) {
      setDrawingEnabled(true);
    } else {
      setDrawingEnabled(false);
    }
  }, [selectedImages?.length]);

  /**
   * @function handleRedo
   * @description Discards the drawn TerraDrawManager instance and baseline, then restarts drawing.
   * @see {@link handleDrawBaseline}
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via the "Redo" button's onClick.
   * @memberof module:ShoreR
   * @returns {void}
   */

  /**
   * @description Lets the user drag the drawn baseline's existing points, re-checking after each
   * adjustment that it still lies inside the AOI — the same rule `handleDrawing` enforces when the
   * baseline is first drawn.
   * @see {@link useShapeVertexEditing}
   * @see {@link areBaselineCoordinatesInsideAOI}
   * @memberof module:ShoreR
   */
  const baselineEditing = useShapeVertexEditing({
    onChange: setBaseLineCoords,
    validate: (coords) => (areBaselineCoordinatesInsideAOI(coords) ? '' : t('forms.map.editOutsideAOI')),
    enabled: BASELINE_VERTEX_EDITING_ENABLED,
  });

  /**
   * @function areBaselineCoordinatesInsideAOI
   * @description Checks whether every `[lng, lat]` pair falls inside the region of interest. The
   * coordinate-based counterpart of `isPolygonInsideAOI`, used while editing, when the new vertices
   * arrive as plain coordinates rather than as an overlay.
   * @param {Array<Array<Number>>} coords - Baseline coordinates as [lng, lat] pairs.
   * @see {@link isPolygonInsideAOI}
   * @memberof module:ShoreR
   * @returns {Boolean} True when the whole baseline is inside the AOI.
   */
  function areBaselineCoordinatesInsideAOI(coords) {
    if (!roiCoords.current) return true;

    return coords.every(([lng, lat]) =>
      window.google.maps.geometry?.poly.containsLocation(new window.google.maps.LatLng(lat, lng), roiCoords.current),
    );
  }

  const handleRedo = () => {
    if (drawingManager.current) {
      drawingManager.current.cleanup();
      drawingManager.current.setMap(null);
      drawingManager.current = null;
    }
    baseLine && baseLine.overlay.setMap(null);
    baselineEditing.setShape(null);
    setBaseLine(null);
    setConfirmDrawing(false);
    setDrawingBaseline(true);
    handleDrawBaseline();
  };

  /**
   * @function handleDrawing
   * @description Validates that the drawn baseline is inside the ROI, then stores it (and its
   * [lng, lat] coordinates) and disables the drawing mode; rejects and removes the overlay
   * otherwise.
   * @param {Object} polyg - Drawing-manager 'overlaycomplete' event, with an `overlay` property.
   * @see {@link isPolygonInsideAOI}
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via the TerraDrawManager's 'overlaycomplete' listener.
   * @memberof module:ShoreR
   * @returns {void}
   */
  function handleDrawing(polyg) {
    if (isPolygonInsideAOI(polyg.overlay, roiCoords.current)) {
      setConfirmDrawing(true);
      setBaseLine(polyg);
      drawingManager.current.setDrawingMode(null);
      dispatch(Map.completeDrawing());
      var coords = polyg.overlay
        .getPath()
        .getArray()
        .map((coord) => [coord.lng(), coord.lat()]);
      setBaseLineCoords(coords);
      baselineEditing.setShape(polyg.overlay, 'polyline');
      setDrawingBaseline(false);
      return;
    }
    notifyError(t('forms.shorelineAnalysis.baselineAOIDrawAlert'));
    polyg.overlay.setMap(null);
  }

  /**
   * @function isPolygonInsideAOI
   * @description Checks whether every vertex of a drawn polyline/polygon falls inside the ROI polygon.
   * @param {google.maps.Polyline|google.maps.Polygon} polygonInner - Drawn shape to check.
   * @param {google.maps.Polygon} polygonOuter - ROI polygon to check against.
   * **Called by:
   * @see {handleDrawing}
   * @memberof module:ShoreR
   * @returns {boolean} True if every vertex is inside the ROI.
   */
  function isPolygonInsideAOI(polygonInner, polygonOuter) {
    for (let i = 0; i < polygonInner.getPath().getLength(); i++) {
      if (!window.google.maps.geometry?.poly.containsLocation(polygonInner.getPath().getAt(i), polygonOuter)) {
        return false;
      }
    }
    return true;
  }

  /**
   * @function handleDrawBaseline
   * @description Initializes (or re-enables) the TerraDrawManager's polyline drawing mode, and
   * clears any previously drawn baseline.
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via the "Draw baseline" button's onClick, and handleRedo.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleDrawBaseline = () => {
    if (!drawingManager.current) {
      drawingManager.current = new TerraDrawManager({
        drawingMode: 'polyline',
        drawingControl: false,
        polylineOptions: {
          strokeColor: '#03d4f0ff',
          strokeOpacity: 1,
          strokeWeight: 3,
        },
      });
      drawingManager.current.setMap(mapRef.current);
      drawingManager.current.addListener('overlaycomplete', handleDrawing);
    } else {
      drawingManager.current.setDrawingMode('polyline');
    }

    if (baseLine) baseLine.overlay.setMap(null);
    setBaseLine(null);
  };

  /**
   * @function importStepExtentTransects
   * @description Infers the step/extent settings from a set of imported transects: the majority
   * (mode) transect length becomes the extent, and the majority distance between consecutive
   * transect centers becomes the step.
   * @param {Array<Array<Array<Number>>>} allTransects - Imported transects, each a pair of [lng, lat] endpoints.
   * @see {@link getMajority}
   * **Called by:
   * @see {readTransectsFileData}
   * @memberof module:ShoreR
   * @returns {void}
   */
  const importStepExtentTransects = (allTransects) => {
    if (!allTransects || allTransects.length === 0) {
      notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
      return;
    }
    /**
     * @function getMajority
     * @description Returns the most frequent value (mode) in an array.
     * @param {Array} array - Values to find the mode of.
     * **Called by:
     * @see {importStepExtentTransects}
     * @memberof module:ShoreR
     * @returns {*} The most frequent value.
     */
    const getMajority = (array) => {
      const count = array.reduce((acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {});
      return Object.keys(count).reduce((a, b) => (count[a] > count[b] ? a : b));
    };

    let extensions = [];
    allTransects?.forEach((transect) => {
      extensions.push(
        turf.distance(turf.point(transect[0]), turf.point(transect[1]), {
          units: 'meters',
        }),
      );
    });

    const extension = Math.round(getMajority(extensions));
    const steps = [];

    const center_transects = allTransects?.map((transect) => {
      const coords_lng = (transect[0][0] + transect[1][0]) / 2;
      const coords_lat = (transect[0][1] + transect[1][1]) / 2;
      return [coords_lng, coords_lat];
    });

    if (center_transects.length < 2) {
      notifyError(t('forms.shorelineAnalysis.transectsSettings.alert.importTransectQuantity'));
      return;
    }
    for (let i = 0; i < center_transects.length - 1; i++) {
      const dist = turf.distance(center_transects[i], center_transects[i + 1], {
        units: 'meters',
      });
      steps.push(dist);
    }
    const step = Math.round(getMajority(steps));

    setTransectsExtent(extension);
    setTransectsStep(step);
  };

  /**
   * @function readFileAndGetCoordinates
   * @description Parses KML file content and extracts one coordinate array per
   * `<coordinates>...</coordinates>` block. Local duplicate of `utils.js`'s exported function of
   * the same name (this component does not import it).
   * @param {String} fileContent - Raw text content of a KML file.
   * **Called by:
   * @see {readBaselineFileData}
   * @see {readTransectsFileData}
   * @memberof module:ShoreR
   * @returns {Array<Array>|undefined} One coordinate array per block (each with 2+ points), or
   * undefined if the file has no `<coordinates>` tag.
   */
  const readFileAndGetCoordinates = (fileContent) => {
    if (fileContent.indexOf('<coordinates>') == -1) {
      notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
      return;
    }
    const coordinatesBlocks = fileContent.split('<coordinates>');
    coordinatesBlocks.shift();
    const allObj = [];
    coordinatesBlocks.forEach((block) => {
      let coordinatesString = block.split('</coordinates>')[0];
      let coordinatesArrayStrings = coordinatesString.split(/\s+/).map((el) => el.trim());
      let coordinatesArray = [];
      coordinatesArrayStrings.forEach((el) => {
        if (el.trim() !== '') {
          let coordinatesValues = el.split(',');
          coordinatesValues = coordinatesValues.map((el) => parseFloat(el));
          if (!isNaN(coordinatesValues[0]) && !isNaN(coordinatesValues[1])) {
            coordinatesArray.push([coordinatesValues[0], coordinatesValues[1]]);
          }
        }
      });
      if (coordinatesArray.length >= 2) allObj.push(coordinatesArray);
    });

    return allObj;
  };

  /**
   * @function readBaselineFileData
   * @description Reads the selected KML file, extracts its baseline coordinates, draws it as a
   * polyline, and validates it via handleDrawing.
   * @param {Event} e - File input change event.
   * @see {@link readFileAndGetCoordinates}
   * @see {@link handleDrawing}
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via the baseline KML file input's onChange.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const readBaselineFileData = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    try {
      reader.onload = (event) => {
        const baselineFile = readFileAndGetCoordinates(reader.result, t, notifyError);
        if (baselineFile.length === 0) {
          notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
          return;
        }
        const formattedCoordinates = baselineFile[0].map((coord) => ({
          lat: coord[1],
          lng: coord[0],
        }));

        const polyline = new window.google.maps.Polyline({
          path: formattedCoordinates,
          strokeColor: '#03d4f0ff',
          strokeOpacity: 1,
          strokeWeight: 3,
          map: mapRef.current,
        });

        handleDrawing({ overlay: polyline });
      };
      if (file) reader.readAsText(file);
    } catch (error) {
      console.error('Error reading baseline file:', error);
      notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
    }
  };

  /**
   * @function readTransectsFileData
   * @description Reads the selected KML file, extracts its transect line coordinates, and infers
   * the step/extent settings from them.
   * @param {Event} e - File input change event.
   * @see {@link readFileAndGetCoordinates}
   * @see {@link importStepExtentTransects}
   * **Called by:
   * @see {ShoreRAnalysisSteps} - via the transects KML file input's onChange.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const readTransectsFileData = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    try {
      reader.onload = async (event) => {
        const allTransects = await readFileAndGetCoordinates(reader.result, t, notifyError);
        if (allTransects?.length === 0) {
          notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
          return;
        }
        importStepExtentTransects(allTransects);
      };
      if (file) reader.readAsText(file);
    } catch (error) {
      console.error('Error reading transects file:', error);
      notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: OVERLAY_PANEL_GAP }}>
      <ShapeEditControl editing={baselineEditing} visible={confirmDrawing} />
      {!analysisDisabled && !isSettings && !drawingBaseline && (
        <Card
          style={{
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
          elevation={3}>
          <div
            style={{
              fontWeight: 'bold',
              borderBottom: '1px solid gray',
              paddingBottom: 5,
              fontSize: 20,
              display: 'flex',
              alignItems: 'center',
            }}>
            {t('forms.shoreR.analysis.title')}
            <IconButton onClick={() => setShowHelpDialog(true)} color="primary" size="small" style={{ marginLeft: 8 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </div>

          <SimpleInfoDialog
            open={showHelpDialog}
            close={() => setShowHelpDialog(false)}
            title={t('forms.transectEvolution.title')}
            text={t('forms.transectEvolution.TransectEvolutionInfoDialog')}
          />

          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}>
            <Button
              style={{ justifyContent: 'left' }}
              disabled={!availableImages?.length}
              color="primary"
              startIcon={<Waves />}
              onClick={() => dispatch(Imagery.imageSelection())}>
              {t('forms.imageChooser.actions.analyzeShoreline.imageSelection.title')}
            </Button>

            <Button
              style={{ justifyContent: 'left' }}
              disabled={!drawingEnabled}
              color="primary"
              startIcon={<GestureOutlined />}
              onClick={() => {
                handleDrawBaseline();
                setDrawingBaseline(true);
              }}>
              {t('forms.csqueeze.result.drawBaseline')}
            </Button>

            <Button
              style={{ justifyContent: 'left' }}
              disabled={!settingsTransectsEnabled}
              color="primary"
              startIcon={<LinearScale />}
              onClick={() => {
                setIsSettings(true);
                const center = bounds.current?.getCenter();
                mapRef.current.setCenter(center);
                setTransectsStep(50);
                setTransectsExtent(500);
              }}>
              {t('forms.csqueeze.result.configTransects')}
            </Button>
          </Box>
        </Card>
      )}

      {!confirmDrawing && drawingBaseline && (
        <Card
          style={{
            width: '100%',
            padding: 12,
            textAlign: 'center',
            marginTop: 10,
            fontSize: 13,
          }}
          elevation={3}>
          {t('forms.csqueeze.result.tipBaseline')}
          {'\n'}
          <Button color="secondary" component="label" htmlFor="file_import_baseline_kml">
            {t('forms.shorelineAnalysis.transectsSettings.importBaseline')}
            <input
              style={{ display: 'none' }}
              id="file_import_baseline_kml"
              accept=".kml"
              type="file"
              onClick={(e) => (e.target.value = null)}
              onChange={readBaselineFileData}
            />
          </Button>
        </Card>
      )}

      <Grow in={drawingEnabled} unmountOnExit>
        <Box position="absolute" marginTop={6} paddingTop={2} justifyContent="center" width="100%" zIndex={99}>
          <Dialog
            open={confirmDrawing}
            onClose={() => setConfirmDrawing(false)}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
            PaperProps={{
              style: { padding: 20, borderRadius: 12, textAlign: 'center' },
            }}>
            <DialogTitle id="confirm-dialog-title" style={{ textAlign: 'center' }}>
              {t('forms.csqueeze.result.confirmBaseline')}
            </DialogTitle>
            <DialogContent style={{ fontSize: '16px' }}>
              <p>{t('forms.csqueeze.result.confirmBaseline')}</p>
            </DialogContent>
            <DialogActions style={{ justifyContent: 'center' }}>
              <Button onClick={handleRedo} color="primary">
                {t('forms.csqueeze.result.redo')}
              </Button>
              <Button
                onClick={() => {
                  setConfirmDrawing(true);
                  setSettingsTransectsEnabled(true);
                  dispatch(Map.completeDrawing());
                  setDrawingEnabled(false);
                }}
                color="primary"
                autoFocus>
                {t('forms.csqueeze.result.confirm')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Grow>

      <Grow in={isSettings} unmountOnExit paddingTop={0}>
        <Box
          id="settingsTransects"
          display="contents"
          position="fixed !important"
          justifyContent="start"
          alignItems="center"
          width="100%"
          zIndex={99}
          style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <Card
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 5,
              padding: 12,
              textAlign: 'center',
            }}
            elevation={3}>
            <div
              style={{
                height: 20,
                display: 'flex',
                justifyContent: 'flex-end',
              }}>
              <Tooltip title={t('forms.imageChooser.helpTooltip')}>
                <IconButton onClick={() => setShowImageChooserHelp(true)} size="small" style={{ padding: 4 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <SimpleInfoDialog
                open={showImageChooserHelp}
                close={() => setShowImageChooserHelp(false)}
                title={t('forms.transectEvolution.title')}
                text={t('forms.transectEvolution.TransectEvolutionInfoDialog')}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 20,
              }}>
              <Typography id="discrete-slider" gutterBottom style={{ fontSize: 15 }}>
                {t('forms.shorelineAnalysis.transectsSettings.setStepLabel')}
              </Typography>
              <Input
                type="number"
                value={transectsStep === 0 ? '' : transectsStep}
                onChange={(e) => {
                  let value = Number(e.target.value);
                  setTransectsStep(isNaN(value) ? 0 : Number(value));
                }}
                placeholder="50"
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 20,
              }}>
              <Typography id="discrete-slider" gutterBottom style={{ fontSize: 15 }}>
                {t('forms.shorelineAnalysis.transectsSettings.setExtentLabel')}
              </Typography>
              <Input
                type="number"
                value={transectsExtent === 0 ? '' : transectsExtent}
                onChange={(e) => {
                  let value = Number(e.target.value);
                  setTransectsExtent(isNaN(value) ? 0 : value);
                }}
                placeholder="500"
              />
            </div>
            <p
              style={{
                padding: 0,
                margin: 0,
              }}>
              {t('forms.shorelineAnalysis.transectsSettings.or')}
            </p>
            <Button color="secondary" component="label" htmlFor="file_import_transects_kml">
              {t('forms.shorelineAnalysis.transectsSettings.importTransectsStepsExtent')}
              <input
                style={{ display: 'none' }}
                id="file_import_transects_kml"
                accept=".kml"
                type="file"
                onClick={(e) => (e.target.value = null)}
                onChange={readTransectsFileData}
              />
            </Button>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 20,
              }}>
              <Typography id="discrete-slider" gutterBottom style={{ fontSize: 15 }}>
                {t('forms.shorelineAnalysis.transectsSettings.setThresholdLabel')}
              </Typography>
              <Input
                type="number"
                value={threshold}
                onChange={(e) => {
                  let value = e.target.value;
                  if (value === '') {
                    setThreshold('');
                    return;
                  }
                  value = Number(value);
                  setThreshold(isNaN(value) ? 0 : value);
                }}
                placeholder="0.01"
              />
            </div>
            <Button
              disabled={!confirmTransectsConfigEnabled}
              color="primary"
              onClick={async () => {
                try {
                  if (threshold == '') setThreshold(0.01);
                  setSettingsTransectsEnabled(false);
                  setIsSettings(false);
                  setAnalysisDisabled(true);
                  await dispatch(
                    Imagery.performShoreRAnalysis(
                      baseLineCoords,
                      transectsStep,
                      transectsExtent,
                      selectedImages,
                      threshold,
                    ),
                  );
                } catch (error) {
                  setAnalysisDisabled(false);
                  console.error('Analysis failed:', error);
                }
              }}
              style={{
                paddingBottom: 0,
                marginBottom: 0,
              }}>
              {t('forms.shorelineAnalysis.transectsSettings.confirm')}
            </Button>
            <Button
              color="secondary"
              style={{
                padding: 0,
                margin: 0,
              }}
              onClick={() => {
                setIsSettings(false);
                transectsObjects.forEach((object) => object.setMap(null));
                setTransectsObjects([]);
              }}>
              {t('forms.csqueeze.result.cancel')}
            </Button>
          </Card>
        </Box>
      </Grow>

      {analysisCompleted && !projectionConfirmed && (
        <ProjectionSettingsCard
          t={t}
          onConfirm={handleProjectionConfirm}
          shorelineDates={getShorelineDates(shoreRData?.shorelinesGeoJSON)}
        />
      )}

      {projectionConfirmed && !bruunActive && (
        <Card style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }} elevation={3}>
          <Button color="primary" variant="contained" fullWidth onClick={() => setExportDialogOpen(true)}>
            {t('forms.shoreR.exports.openButton')}
          </Button>
          {!bruunConfirmed ? (
            <Button color="primary" fullWidth onClick={handleBruunOpen}>
              {t('forms.shoreR.bruun.openButton')}
            </Button>
          ) : (
            <Button color="primary" fullWidth onClick={handleUndoBruun}>
              {t('forms.shoreR.bruun.undo')}
            </Button>
          )}
          <Button color="primary" fullWidth onClick={handleRedoLastStep}>
            {t('forms.shoreR.redoLastStep')}
          </Button>
          <Button color="secondary" fullWidth onClick={handleRedoAnalysis}>
            {t('forms.shoreR.redoAnalysis')}
          </Button>
        </Card>
      )}

      <Dialog open={bruunActive} onClose={() => setBruunActive(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ flex: 1 }}>{t('forms.shoreR.bruun.title')}</span>
            <Tooltip title={t('forms.shoreR.bruun.helpTooltip')}>
              <IconButton size="small" onClick={() => setBruunHelpOpen(true)}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </DialogTitle>
        <DialogContent>
          <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '10px 14px', marginBottom: 16 }}>
            <Typography variant="caption" style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>
              {t('forms.shoreR.bruun.formulaLabel')}
            </Typography>
            <Typography style={{ fontFamily: 'monospace', fontSize: 15 }}>{'Ra = (SLRa × L) / (h* + DC)'}</Typography>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <Typography style={{ fontSize: 13, marginBottom: 4 }}>{t('forms.shoreR.bruun.horizonLabel')}</Typography>
              <FormControl style={{ minWidth: 120 }}>
                <Select value={bruunHorizon ?? ''} onChange={(e) => setBruunHorizon(Number(e.target.value))}>
                  {(projectionStateRef.current?.horizons ?? []).map((h) => (
                    <MenuItem key={h} value={h}>
                      {h} {t('forms.shoreR.projection.yearUnit')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography style={{ fontSize: 13, flex: 1 }}>{t('forms.shoreR.bruun.slraLabel')}</Typography>
              <Input
                type="number"
                value={bruunSLRa}
                onChange={(e) => setBruunSLRa(Number(e.target.value))}
                inputProps={{ min: 0, step: 0.1 }}
                style={{ width: 80 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography style={{ fontSize: 13, flex: 1 }}>{t('forms.shoreR.bruun.lLabel')}</Typography>
              <Input
                type="number"
                value={bruunL}
                onChange={(e) => setBruunL(e.target.value)}
                inputProps={{ min: 0 }}
                style={{ width: 80 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography style={{ fontSize: 13, flex: 1 }}>{t('forms.shoreR.bruun.hStarLabel')}</Typography>
              <Input
                type="number"
                value={bruunHStar}
                onChange={(e) => setBruunHStar(e.target.value)}
                inputProps={{ min: 0 }}
                style={{ width: 80 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography style={{ fontSize: 13, flex: 1 }}>{t('forms.shoreR.bruun.dcLabel')}</Typography>
              <Input
                type="number"
                value={bruunDC}
                onChange={(e) => setBruunDC(e.target.value)}
                inputProps={{ min: 0 }}
                style={{ width: 80 }}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setBruunActive(false)}>
            {t('forms.csqueeze.result.cancel')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!bruunHorizon || !Number(bruunL) || Number(bruunHStar) + Number(bruunDC) <= 0}
            onClick={handleBruunConfirm}>
            {t('forms.shoreR.bruun.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <SimpleInfoDialog
        open={bruunHelpOpen}
        close={() => setBruunHelpOpen(false)}
        title="forms.shoreR.bruun.helpTooltip"
        text="forms.shoreR.bruun.helpContent"
      />

      <Dialog open={bruunActive} onClose={() => setBruunActive(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ flex: 1 }}>{t('forms.shoreR.bruun.title')}</span>
            <Tooltip title={t('forms.shoreR.bruun.helpTooltip')}>
              <IconButton size="small" onClick={() => setBruunHelpOpen(true)}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </DialogTitle>
        <DialogContent>
          <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '10px 14px', marginBottom: 16 }}>
            <Typography variant="caption" style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>
              {t('forms.shoreR.bruun.formulaLabel')}
            </Typography>
            <Typography style={{ fontFamily: 'monospace', fontSize: 15 }}>{'Ra = (SLRa × L) / (h* + DC)'}</Typography>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <Typography style={{ fontSize: 13, marginBottom: 4 }}>{t('forms.shoreR.bruun.horizonLabel')}</Typography>
              <FormControl style={{ minWidth: 120 }}>
                <Select value={bruunHorizon ?? ''} onChange={(e) => setBruunHorizon(Number(e.target.value))}>
                  {(projectionStateRef.current?.horizons ?? []).map((h) => (
                    <MenuItem key={h} value={h}>
                      {h} {t('forms.shoreR.projection.yearUnit')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography style={{ fontSize: 13, flex: 1 }}>{t('forms.shoreR.bruun.slraLabel')}</Typography>
              <Input
                type="number"
                value={bruunSLRa}
                onChange={(e) => setBruunSLRa(Number(e.target.value))}
                inputProps={{ min: 0, step: 0.1 }}
                style={{ width: 80 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography style={{ fontSize: 13, flex: 1 }}>{t('forms.shoreR.bruun.lLabel')}</Typography>
              <Input
                type="number"
                value={bruunL}
                onChange={(e) => setBruunL(e.target.value)}
                inputProps={{ min: 0 }}
                style={{ width: 80 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography style={{ fontSize: 13, flex: 1 }}>{t('forms.shoreR.bruun.hStarLabel')}</Typography>
              <Input
                type="number"
                value={bruunHStar}
                onChange={(e) => setBruunHStar(e.target.value)}
                inputProps={{ min: 0 }}
                style={{ width: 80 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography style={{ fontSize: 13, flex: 1 }}>{t('forms.shoreR.bruun.dcLabel')}</Typography>
              <Input
                type="number"
                value={bruunDC}
                onChange={(e) => setBruunDC(e.target.value)}
                inputProps={{ min: 0 }}
                style={{ width: 80 }}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setBruunActive(false)}>
            {t('forms.csqueeze.result.cancel')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!bruunHorizon || !Number(bruunL) || Number(bruunHStar) + Number(bruunDC) <= 0}
            onClick={handleBruunConfirm}>
            {t('forms.shoreR.bruun.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <SimpleInfoDialog
        open={bruunHelpOpen}
        close={() => setBruunHelpOpen(false)}
        title="forms.shoreR.bruun.helpTooltip"
        text="forms.shoreR.bruun.helpContent"
      />

      <ShoreRExportDialog open={exportDialogOpen} close={() => setExportDialogOpen(false)} />

      <OutlierShorelinesDialog
        open={outlierDialogOpen}
        outlierShorelines={outlierShorelines}
        onConfirm={handleOutlierConfirm}
        onToggle={handleOutlierToggle}
        onClose={handleOutlierClose}
        t={t}
      />
    </div>
  );
};

export default ShoreRAnalysisSteps;
